import React, { useState } from 'react';
import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TaggedFitting, fetchFitting } from '../api/fittings';
import { shipRender, typeIcon } from '../services/eveImages';
import Chevron from './Chevron';
import FittingStatusBadge from './FittingStatusBadge';
import StatusBadge from './StatusBadge';
import { C, FONT, glow, hexToRgba, lcarsLabelSx, prefersReducedMotion } from '../theme/tokens';

/**
 * What to bring, sitting beside what you are bringing it to.
 *
 * This used to be a full-width strip below the site's panels: a list of fit
 * names with a recommendation tag, no hull, no numbers, and a click that
 * navigated away to the Fittings page — losing the site you were reading. So
 * the one panel whose entire job is "answer the question the other three
 * panels just raised" was the only one you had to leave the page to use.
 *
 * Now it is the fourth panel in the grid, and the answer opens in place: a
 * row carries the hull it flies, how proven it is, and on expansion the four
 * numbers you actually compare a fit against the site's own threat block
 * with — EHP against peak alpha, DPS against total EHP. Comparing those two
 * panels side by side is the whole point of the page.
 */
const nf = new Intl.NumberFormat('en-US');
const compact = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}m` : n >= 10_000 ? `${Math.round(n / 1000)}k` : nf.format(Math.round(n));
const isk = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)}b` : n >= 1e6 ? `${(n / 1e6).toFixed(0)}m` : compact(n));

const SuggestedFittings: React.FC<{ fittings: TaggedFitting[] }> = ({ fittings }) => {
  // One open at a time. These are alternatives to each other, so the useful
  // comparison is fit-against-site, not fit-against-fit; letting three
  // expand at once buries the site's own numbers off-screen.
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <Stack spacing={0.5}>
      {fittings.map((fit, i) => (
        <FittingRow
          key={fit.id}
          fit={fit}
          index={i}
          open={openId === fit.id}
          onToggle={() => setOpenId(openId === fit.id ? null : fit.id)}
        />
      ))}
    </Stack>
  );
};

const FittingRow: React.FC<{ fit: TaggedFitting; index: number; open: boolean; onToggle: () => void }> = ({
  fit,
  index,
  open,
  onToggle,
}) => {
  const reduced = prefersReducedMotion();
  // Fetched only once a row is actually opened, and cached by id afterwards.
  // The stats behind this are an ESI dogma walk per fit; paying for four of
  // them on every site page view to fill numbers nobody expanded would be
  // the expensive way to be slower.
  const detail = useQuery({
    queryKey: ['fitting', fit.id],
    queryFn: () => fetchFitting(fit.id),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: `1px solid ${open ? hexToRgba(C.blue, 0.45) : 'transparent'}`,
        backgroundColor: open ? hexToRgba(C.blue, 0.07) : 'transparent',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        animation: reduced ? 'none' : `fitIn 0.4s cubic-bezier(0.16,1,0.3,1) ${index * 70}ms both`,
        '@keyframes fitIn': {
          '0%': { opacity: 0, transform: 'translateY(6px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover': { backgroundColor: hexToRgba(C.blue, open ? 0.1 : 0.06) },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          cursor: 'pointer',
          p: 1,
          outline: 'none',
          borderRadius: '8px',
          '&:focus-visible': { boxShadow: `0 0 0 2px ${C.cyan}` },
        }}
      >
        <HullArt typeId={fit.ship_type_id} name={fit.ship_name} lit={open} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: open ? C.textBright : C.text,
              fontWeight: 600,
              fontSize: '0.9rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              transition: 'color 0.2s ease',
            }}
          >
            {fit.name}
          </Typography>
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem' }}>
            {fit.ship_name}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
          <FittingStatusBadge status={fit.status} />
          {/* "minimum" is a floor, not a preference — it earns the warning
              colour so it never reads as the safer of the two. */}
          <Tooltip
            title={
              fit.recommendation === 'minimum'
                ? 'The least that survives here. Anything weaker is a loss waiting to happen.'
                : 'What the corp suggests bringing to this site.'
            }
          >
            <span>
              <StatusBadge
                label={fit.recommendation}
                accent={fit.recommendation === 'minimum' ? C.gold : C.cyan}
              />
            </span>
          </Tooltip>
          <Chevron
            color={open ? C.cyan : C.textDim}
            size={8}
            sx={{
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: reduced ? 'none' : 'transform 0.25s cubic-bezier(0.16,1,0.3,1), color 0.2s ease',
            }}
          />
        </Stack>
      </Stack>

      {open && (
        <Box
          sx={{
            px: 1,
            pb: 1.25,
            animation: reduced ? 'none' : 'fitOpen 0.35s cubic-bezier(0.16,1,0.3,1) both',
            '@keyframes fitOpen': {
              '0%': { opacity: 0, transform: 'translateY(-4px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <FittingDetailStrip fittingId={fit.id} query={detail} />
        </Box>
      )}
    </Box>
  );
};

/** The hull, as the hull. A fit is chosen by ship first and name second, so
 * the render is the identifying mark and the name is the caption — not the
 * other way round. Falls back to the flat inventory icon if the render 404s
 * (not every type has one), and to a lettered plate if there is no type id
 * at all, so a row never shows a broken image. */
const HullArt: React.FC<{ typeId: number | null; name: string; lit: boolean }> = ({ typeId, name, lit }) => {
  const [src, setSrc] = useState(typeId ? shipRender(typeId, 128) : null);

  const frame = {
    width: 46,
    height: 46,
    flexShrink: 0,
    borderRadius: '6px',
    overflow: 'hidden',
    border: `1px solid ${hexToRgba(C.blue, lit ? 0.5 : 0.25)}`,
    backgroundColor: hexToRgba(C.blue, 0.08),
    boxShadow: lit ? glow(C.blue, 12, 0.35) : 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.25s ease',
  } as const;

  if (!src) {
    return (
      <Box sx={{ ...frame, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: FONT.display, fontSize: '1.1rem', color: hexToRgba(C.blue, 0.8) }}>
          {name.slice(0, 1).toUpperCase()}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={frame}>
      <Box
        component="img"
        src={src}
        alt=""
        loading="lazy"
        onError={() => setSrc(typeId ? typeIcon(typeId, 64) : null)}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
};

/** The numbers you compare against the site's own threat block, and nothing
 * else. A full fit readout already exists on the Fittings page; repeating it
 * here would bury the four figures that actually decide "does this hold". */
const FittingDetailStrip: React.FC<{
  fittingId: number;
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchFitting>>>>;
}> = ({ fittingId, query }) => {
  const navigate = useNavigate();

  if (query.isLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
        <CircularProgress size={14} sx={{ color: C.blue }} />
        <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>Reading the fit&hellip;</Typography>
      </Stack>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Typography sx={{ color: C.textDim, fontSize: '0.78rem', py: 1 }}>
        Couldn&rsquo;t load this fit&rsquo;s stats. It may have been deleted or unshared.
      </Typography>
    );
  }

  const { stats, isk: price } = query.data;

  return (
    <Box>
      {stats ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(74px, 1fr))',
            gap: 1.25,
            py: 0.5,
          }}
        >
          <Figure
            label="ehp"
            value={compact(stats.tank.totalEhp)}
            accent={C.cyan}
            hint={`${nf.format(Math.round(stats.tank.totalEhp))} effective HP — hold this against the site's peak alpha above.`}
          />
          <Figure
            label="dps"
            value={compact(stats.offense.totalDps)}
            accent={C.red}
            hint={`${nf.format(Math.round(stats.offense.totalDps))} DPS — divide the site's total EHP by this for a rough clear time.`}
          />
          <Figure
            label="sig"
            value={nf.format(Math.round(stats.navigation.signatureRadius))}
            accent={C.gold}
            hint="Signature radius. Smaller is harder for Sleeper missiles to apply full damage to."
          />
          <Figure
            label="align"
            value={stats.navigation.alignTime ?? '—'}
            accent={C.textDim}
            hint="Align time — how long between hitting warp and actually leaving."
          />
          {price && (
            <Figure
              label="cost"
              value={isk(price.total)}
              accent={C.green}
              hint={`${nf.format(Math.round(price.total))} ISK${price.incomplete ? ' — a floor; at least one module had no price.' : ''}`}
            />
          )}
        </Box>
      ) : (
        <Typography sx={{ color: C.textDim, fontSize: '0.78rem', py: 0.5 }}>
          No stats for this fit — EVE no longer resolves its hull, usually a CCP rename.
        </Typography>
      )}

      <Typography
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/fittings?fit=${fittingId}`);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/fittings?fit=${fittingId}`);
          }
        }}
        sx={{
          ...lcarsLabelSx(C.cyan, '0.66rem'),
          display: 'inline-block',
          mt: 1,
          cursor: 'pointer',
          outline: 'none',
          '&:hover': { color: C.textBright },
          '&:focus-visible': { color: C.textBright, textDecoration: 'underline' },
        }}
      >
        Full fit &amp; modules &rarr;
      </Typography>
    </Box>
  );
};

const Figure: React.FC<{ label: string; value: string; accent: string; hint: string }> = ({
  label,
  value,
  accent,
  hint,
}) => (
  <Tooltip title={hint}>
    <Box sx={{ minWidth: 0, cursor: 'help' }}>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.56rem') }}>{label}</Typography>
      <Typography
        sx={{ fontFamily: FONT.mono, fontWeight: 700, fontSize: '0.95rem', color: accent, lineHeight: 1.2 }}
      >
        {value}
      </Typography>
    </Box>
  </Tooltip>
);

export default SuggestedFittings;
